import type Dexie from 'dexie';
import type { Table } from 'dexie';
import { api, ApiError, isOffline } from '../api/client';

/**
 * A generic offline-sync runner, for modules built on the platform sync core.
 *
 * It is the groceries `sync/engine.ts` core distilled to what any module needs:
 * pull → push → pull, a coalescing loop on the events that mean something
 * changed (back online, back on screen, a slow poll), and a `since` watermark
 * taken from the server's `server_time`, never the client clock.
 *
 * A module supplies its own Dexie database, outbox, meta store and table map;
 * the two modules therefore share no outbox, no cursor and no transaction — a
 * backfill or a bug on one side cannot stall the other.
 *
 * Agenda tombstones are retained (never purged server-side), so a `full_resync`
 * still carries every delete and no local prune is needed — the runner simply
 * upserts what it is sent, which is the whole of the reconciliation.
 */

export type SyncState = 'idle' | 'syncing' | 'offline' | 'signed_out' | 'error';

export interface OutboxEntry {
  seq?: number;
  mutation: { id: string } & Record<string, unknown>;
  queued_at: string;
}
export interface MetaRow {
  key: string;
  value: unknown;
}
export interface RejectionRow {
  id: string;
  code: string;
  message: string;
  at: string;
  mutation: unknown;
}

interface PullResp {
  server_time: string;
  changes: Record<string, Record<string, unknown>[]>;
  has_more: boolean;
  cursor?: string;
  full_resync?: boolean;
}
interface PushResp {
  applied: string[];
  rejected: { id: string; code: string; message: string }[];
  server_time: string;
}

export interface RunnerConfig {
  /** The sync endpoint, e.g. `/api/v1/agenda/sync`. */
  endpoint: string;
  db: Dexie;
  outbox: Table<OutboxEntry, number>;
  meta: Table<MetaRow, string>;
  /** Where a validation-rejected mutation is kept, if the module surfaces them. */
  rejections?: Table<RejectionRow, string>;
  /** Change-bucket name → the Dexie table it is written into. */
  tables: { name: string; table: Table<Record<string, unknown>, string> }[];
  /** Meta keys, namespaced by the module so two runners never collide. */
  sinceKey: string;
  lastOkKey: string;
}

export interface SyncRunner {
  sync(): Promise<void>;
  startLoop(): () => void;
  onState(listener: (s: SyncState) => void): () => void;
  lastSyncedAt(): Promise<string | undefined>;
  pendingCount(): Promise<number>;
}

const BATCH = 200;
const POLL_MS = 60_000;

export function createSyncRunner(cfg: RunnerConfig): SyncRunner {
  const listeners = new Set<(s: SyncState) => void>();
  let current: SyncState = 'idle';
  let running = false;
  let queuedAgain = false;

  const setState = (s: SyncState) => {
    current = s;
    for (const l of listeners) l(s);
  };

  const getMeta = async <T>(key: string): Promise<T | undefined> =>
    (await cfg.meta.get(key))?.value as T | undefined;
  const setMeta = (key: string, value: unknown) => cfg.meta.put({ key, value });

  async function applyChanges(changes: PullResp['changes']): Promise<void> {
    await cfg.db.transaction(
      'rw',
      cfg.tables.map((t) => t.table),
      async () => {
        for (const { name, table } of cfg.tables) {
          const rows = changes[name];
          // Rows arrive whole, tombstones included, and are stored as sent —
          // filtered at read time, never deleted here.
          if (rows && rows.length) await table.bulkPut(rows as never[]);
        }
      },
    );
  }

  async function pull(): Promise<void> {
    const since = await getMeta<string>(cfg.sinceKey);
    let cursor: string | undefined;
    let serverTime: string | undefined;

    for (let page = 0; page < 50; page += 1) {
      const params = new URLSearchParams();
      if (since) params.set('since', since);
      if (cursor) params.set('cursor', cursor);
      const resp = await api<PullResp>(`${cfg.endpoint}?${params.toString()}`);
      await applyChanges(resp.changes);
      serverTime = resp.server_time;
      if (!resp.has_more) break;
      cursor = resp.cursor;
      if (!cursor) break;
    }
    // Advanced only after the whole set has landed.
    if (serverTime) await setMeta(cfg.sinceKey, serverTime);
  }

  async function push(): Promise<void> {
    for (;;) {
      const batch = await cfg.outbox.orderBy('seq').limit(BATCH).toArray();
      if (batch.length === 0) return;

      const resp = await api<PushResp>(cfg.endpoint, {
        method: 'POST',
        body: { mutations: batch.map((e) => e.mutation) },
      });
      const applied = new Set(resp.applied);
      const rejected = new Map(resp.rejected.map((r) => [r.id, r]));

      await cfg.db.transaction(
        'rw',
        cfg.rejections ? [cfg.outbox, cfg.rejections] : [cfg.outbox],
        async () => {
          for (const entry of batch) {
            const id = entry.mutation.id;
            if (applied.has(id)) {
              await cfg.outbox.delete(entry.seq!);
            } else if (rejected.has(id)) {
              const r = rejected.get(id)!;
              // A rejection is a validation failure, not a conflict — retrying
              // forever would wedge the queue, so it is recorded and dropped.
              if (cfg.rejections) {
                await cfg.rejections.put({
                  ...r,
                  at: new Date().toISOString(),
                  mutation: entry.mutation,
                });
              }
              await cfg.outbox.delete(entry.seq!);
            }
          }
        },
      );

      if (batch.length < BATCH) return;
    }
  }

  async function sync(): Promise<void> {
    if (running) {
      queuedAgain = true;
      return;
    }
    running = true;
    setState('syncing');
    try {
      await pull();
      await push();
      // The second pull brings back the server's version of what was just sent,
      // including anything the other phone did in between.
      await pull();
      await setMeta(cfg.lastOkKey, new Date().toISOString());
      setState('idle');
    } catch (err) {
      if (isOffline(err)) setState('offline');
      else if (err instanceof ApiError && err.isUnauthenticated) setState('signed_out');
      else {
        setState('error');
        console.error(`sync failed for ${cfg.endpoint}`, err);
      }
    } finally {
      running = false;
      if (queuedAgain) {
        queuedAgain = false;
        void sync();
      }
    }
  }

  function startLoop(): () => void {
    const kick = () => void sync();
    const onVisible = () => {
      if (document.visibilityState === 'visible') kick();
    };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') kick();
    }, POLL_MS);
    kick();
    return () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }

  return {
    sync,
    startLoop,
    onState: (listener) => {
      listeners.add(listener);
      listener(current);
      return () => listeners.delete(listener);
    },
    lastSyncedAt: () => getMeta<string>(cfg.lastOkKey),
    pendingCount: () => cfg.outbox.count(),
  };
}
